import { isAxiosError } from 'axios'
import { useFormik } from 'formik'
import { useContext } from 'react'
import { useQuery } from 'react-query'
import { toast } from 'react-toastify'
import * as Yup from 'yup'

import { handleException } from 'clients/exception-handling'
import { AuthContext } from 'features/auth'
import { useCanShareWith } from 'features/share/hooks'
import type { ShareService } from 'features/share/service'

const validationSchema = Yup.object().shape({
  shared_email: Yup.string()
    .email('Invalid email')
    .required('Field is required'),
  first_name: Yup.string().required('Field is required'),
  last_name: Yup.string().required('Field is required'),
  org: Yup.string().required('Field is required'),
  team: Yup.string().required('Field is required'),
})

// The API explains a refusal (a locked hub, a bad address) in `detail`; the
// axios message alone is just "Request failed with status code 403".
function errorMessage(e: unknown): string {
  if (isAxiosError(e)) {
    const detail = e.response?.data?.detail
    if (typeof detail === 'string' && detail.length > 0) return detail
  }
  return (e as Error).message
}

export function useShareForm(
  service: ShareService,
  projectId: string,
  email: string | null,
  onSuccess: () => void,
): {
  formik: ReturnType<
    typeof useFormik<{
      role: string
      last_name: string
      first_name: string
      shared_email: string
      team: string
      org: string
    }>
  >
} {
  const { userDetails } = useContext(AuthContext)
  const canShareWith = useCanShareWith(service, projectId)

  const { refetch } = useQuery(
    ['shared-users', projectId],
    () => service.fetchSharedUsers(projectId),
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      staleTime: Infinity,
      cacheTime: Infinity,
      enabled: !!projectId,
    },
  )

  const formik = useFormik({
    validationSchema,
    validateOnChange: false,
    initialValues: {
      role: 'viewer',
      last_name: '',
      first_name: '',
      shared_email: email || '',
      team: userDetails?.team || '',
      org: userDetails?.org || '',
    },
    onSubmit: async ({ role, ...values }, { setFieldError }) => {
      if (!canShareWith(values.shared_email)) {
        setFieldError('shared_email', 'You are not allowed to invite this user')
        return
      }

      try {
        await service.shareProjectWithUser(projectId, {
          ...values,
          admin: role === 'admin',
          create_user: true,
        })
        await refetch()
        toast.success('User has been invited successfully')
        onSuccess()
      } catch (e: unknown) {
        handleException(e, { operation: 'project.share.create-user' })

        toast.error(errorMessage(e))
      }
    },
  })

  return { formik }
}
